import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface RelayDevice {
    id: number;
    name: string;
    deviceId: string;
    macAddress?: string;
    status: string;
}

interface RelayModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (e: React.FormEvent) => void;
    formData: {
        id?: number;
        name: string;
        deviceId: string;
        macAddress: string;
        status: string;
    };
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    loading: boolean;
    isEdit: boolean;
}

export const RelayDeviceModal: React.FC<RelayModalProps> = ({
    show,
    onHide,
    onSubmit,
    formData,
    handleInputChange,
    loading,
    isEdit
}) => {
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>{isEdit ? 'Edit Relay Device' : 'Add New Relay Device'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={onSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="relayName">
                                <Form.Label>Device Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Main Gate Relay"
                                    required
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="relayDeviceId">
                                <Form.Label>Device ID (eWeLink) <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="deviceId"
                                    value={formData.deviceId}
                                    onChange={handleInputChange}
                                    placeholder="Enter physical device ID"
                                    required
                                />
                            </Form.Group>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="relayMac">
                                <Form.Label>MAC Address</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="macAddress"
                                    value={formData.macAddress}
                                    onChange={handleInputChange}
                                    placeholder="XX:XX:XX:XX:XX:XX"
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="relayStatus">
                                <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="INACTIVE">INACTIVE</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                    <div className="text-end mt-4">
                        <Button variant="light" onClick={onHide} className="me-2" disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Processing...' : isEdit ? 'Update Relay' : 'Add Relay'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

interface ViewRelayModalProps {
    show: boolean;
    onHide: () => void;
    relay: RelayDevice | null;
}

export const ViewRelayModal: React.FC<ViewRelayModalProps> = ({
    show,
    onHide,
    relay
}) => {
    if (!relay) return null;
    
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Relay Device Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="row g-3">
                    <div className="col-12 border-bottom pb-2">
                        <label className="text-muted small d-block">Device Name</label>
                        <span className="fw-bold fs-5">{relay.name}</span>
                    </div>
                    <div className="col-md-6 border-bottom pb-2">
                        <label className="text-muted small d-block">Device ID</label>
                        <code>{relay.deviceId}</code>
                    </div>
                    <div className="col-md-6 border-bottom pb-2">
                        <label className="text-muted small d-block">MAC Address</label>
                        <span>{relay.macAddress || 'N/A'}</span>
                    </div>
                    <div className="col-md-6 border-bottom pb-2">
                        <label className="text-muted small d-block">Status</label>
                        <span className={`badge bg-${relay.status === 'ACTIVE' ? 'success' : 'danger'}-subtle text-${relay.status === 'ACTIVE' ? 'success' : 'danger'}`}>
                            {relay.status}
                        </span>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

interface DeleteModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    deviceName: string;
    loading: boolean;
}

export const DeleteRelayModal: React.FC<DeleteModalProps> = ({
    show,
    onHide,
    onConfirm,
    deviceName,
    loading
}) => {
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Delete Relay Device</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                <div className="text-danger mb-4">
                    <i className="ti ti-trash-x fs-1" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5>Are you sure?</h5>
                <p className="text-muted">You are about to delete <strong>{deviceName}</strong>. This action cannot be undone.</p>
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center">
                <Button variant="light" onClick={onHide} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={loading}>
                    {loading ? 'Deleting...' : 'Yes, Delete It'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
